// frontend/src/pages/ExportChartByRegionPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import Loading from '../../components/Loading';
import DateRangeControls from '../../components/DateRangeControls';
import useDateRange from '../../hooks/useDateRange';
// [수정] createDynamicDualAxisOptions import
import { createDynamicDualAxisOptions } from '../../utils/chartOptions';

import { Typography, Card, Select, Button, Space, Tag, message, Divider, Checkbox, Radio, Empty, Tooltip } from 'antd';
import { 
    EnvironmentOutlined, 
    SearchOutlined, 
    GlobalOutlined, 
    StockOutlined,
    CameraOutlined,
    FileExcelOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Text } = Typography;
const { Title } = ResponsiveTypography;

const ExportChartByRegionPage = () => {
    const [hscodeOptions, setHscodeOptions] = useState([]);
    const [sidoSggData, setSidoSggData] = useState({});
    const [hscode, setHscode] = useState(null);
    const [sido, setSido] = useState(null);
    const [sgg, setSgg] = useState(null);
    const [selectedRegions, setSelectedRegions] = useState([
        { id: `전체-전체-${Date.now()}`, sido: '전체', sgg: '전체' }
    ]);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [stockOptions, setStockOptions] = useState([]);
    const [selectedStocks, setSelectedStocks] = useState([]);
    const [dataType, setDataType] = useState('Value');
    const [periodType, setPeriodType] = useState('TTM');
    const [isShifted, setIsShifted] = useState(true);
    
    // [중요] 지역 데이터 합산 상태
    const [isAggregated, setIsAggregated] = useState(false);
    
    const chartRef = useRef(null);

    const combinedDataForDateRange = useMemo(() => {
        if (!chartData) return [];
        const allTradeData = chartData.tradeData ? chartData.tradeData.flatMap(region => region.data) : [];
        const allStockData = chartData.stockData ? chartData.stockData.flatMap(stock => stock.data) : [];
        return allTradeData.concat(allStockData);
    }, [chartData]);

    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(combinedDataForDateRange, { dateColumn: 'Date', defaultPeriod: 'ALL' });

    // --- 데이터 합산 및 YoY 계산 로직 ---
    const processedChartData = useMemo(() => {
        if (!chartData) return null;
        if (!isAggregated || !chartData.tradeData || chartData.tradeData.length === 0) {
            return chartData;
        }

        // 1. 합산 (Value)
        const dateMap = new Map();
        chartData.tradeData.forEach(region => {
            region.data.forEach(point => {
                const currentVal = dateMap.get(point.Date) || 0;
                dateMap.set(point.Date, currentVal + (point.value || 0));
            });
        });

        const joinedRegionName = chartData.tradeData.map(r => r.regionName).join(' + ');

        let aggregatedData = Array.from(dateMap.entries())
            .map(([Date, value]) => ({ Date, value }))
            .sort((a, b) => new Date(a.Date) - new Date(b.Date));

        // 2. YoY 계산 (옵션 선택 시)
        let finalUnit = chartData.unit; 
        
        if (dataType === 'Growth') {
            finalUnit = '%, YoY';
            const valueMap = new Map(aggregatedData.map(item => [item.Date, item.value]));
            
            aggregatedData = aggregatedData.map(item => {
                const currentYearStr = item.Date.substring(0, 4);
                const prevYearStr = (parseInt(currentYearStr, 10) - 1).toString();
                const prevDateStr = item.Date.replace(currentYearStr, prevYearStr);
                const prevValue = valueMap.get(prevDateStr);
                
                let growth = null;
                if (prevValue !== undefined && prevValue !== null && prevValue !== 0) {
                    growth = ((item.value - prevValue) / prevValue) * 100;
                }
                return { Date: item.Date, value: growth };
            });
        }

        return {
            ...chartData,
            unit: finalUnit, // 계산된 단위(%, YoY) 전달
            tradeData: [{
                regionName: joinedRegionName, 
                data: aggregatedData
            }]
        };
    }, [chartData, isAggregated, dataType]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setInitLoading(true);
                const [hsRes, sidoSggRes, stockRes] = await Promise.all([
                    apiClient.get('/trade-by-region/hscodes'),
                    apiClient.get('/trade-by-region/sido-sgg'),
                    apiClient.get('/trade-by-region/stock-list')
                ]);
                const formattedHsOptions = hsRes.data.map(item => ({
                    label: `(${item.hsCode}) ${item.품목명} / ${item.세세분류명}`,
                    value: item.hsCode,
                    itemName: item.품목명 
                }));
                setHscodeOptions(formattedHsOptions);
                const defaultHsCode = '190230';
                setHscode(defaultHsCode);
                setSidoSggData(sidoSggRes.data);
                setStockOptions(stockRes.data);
            } catch (error) {
                const errorMsg = error.response?.data?.detail || '초기 데이터 로딩에 실패했습니다.';
                message.error(errorMsg);
            } finally {
                setInitLoading(false);
            }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        const fetchChartData = async () => {
            if (!hscode || selectedRegions.length === 0) {
                setChartData(null);
                return;
            }
            setLoading(true);
            try {
                const effectiveDataType = isAggregated ? 'value' : dataType.toLowerCase();
                const payload = {
                    hscode: hscode,
                    regions: selectedRegions,
                    data_type: effectiveDataType, 
                    period_type: periodType.toLowerCase(),
                    stock_options: selectedStocks,
                    shift_days: isShifted,
                };
                const res = await apiClient.post('/trade-by-region/trade-data', payload);
                setChartData(res.data);
            } catch (error) {
                const errorMsg = error.response?.data?.detail || '차트 데이터 생성에 실패했습니다.';
                message.error(errorMsg);
                setChartData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchChartData();
    }, [hscode, selectedRegions, selectedStocks, dataType, periodType, isShifted, isAggregated]);

    // ... (Sido, Sgg, Region Add/Remove, Download Handlers - 기존과 100% 동일)
    const sidoOptions = useMemo(() => [
        { label: '전국(전체)', value: '전체' },
        ...Object.keys(sidoSggData).map(s => ({ label: s, value: s }))
    ], [sidoSggData]);

    const sggOptions = useMemo(() => {
        if (!sido || sido === '전체') return [{ label: '전체', value: '전체' }];
        const sggs = sidoSggData[sido] || [];
        return [{ label: '전체', value: '전체' }, ...sggs.map(s => ({ label: s, value: s }))];
    }, [sido, sidoSggData]);

    const handleSidoChange = (value) => { setSido(value); setSgg('전체'); };

    const handleAddRegion = () => {
        if (!sido) return message.warning('광역지자체를 선택해주세요.');
        const newRegion = { id: `${sido}-${sgg || '전체'}-${Date.now()}`, sido: sido, sgg: sgg || '전체' };
        if (selectedRegions.some(r => r.sido === newRegion.sido && r.sgg === newRegion.sgg)) return message.info('이미 추가된 지역입니다.');
        setSelectedRegions([...selectedRegions, newRegion]);
    };

    const handleRemoveRegion = (removedId) => setSelectedRegions(selectedRegions.filter(region => region.id !== removedId));

    const getFileName = (ext) => {
        const dateStr = new Date().toISOString().split('T')[0];
        const selectedOption = hscodeOptions.find(opt => opt.value === hscode);
        const itemName = selectedOption ? selectedOption.itemName : hscode;
        return `${itemName}_ByRegion_${dateStr}.${ext}`;
    };

    const handleDownloadPNG = () => {
        if (chartRef.current) {
            const instance = chartRef.current.getEchartsInstance();
            const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff', excludeComponents: ['toolbox'] });
            const link = document.createElement('a'); link.href = url; link.download = getFileName('png'); link.click();
        }
    };

    const handleDownloadData = (format) => {
        const dataToDownload = processedChartData || chartData;
        if (!dataToDownload) return;
        const { tradeData, stockData } = dataToDownload;
        const dateSet = new Set();
        tradeData.forEach(region => region.data.forEach(d => dateSet.add(d.Date)));
        if (stockData) stockData.forEach(stock => stock.data.forEach(d => dateSet.add(d.Date)));
        const sortedDates = Array.from(dateSet).sort();

        let csvContent = "Date";
        tradeData.forEach(r => csvContent += `,${r.regionName} (${dataType}-${periodType})`);
        if (stockData) stockData.forEach(s => csvContent += `,${s.name} Price`);
        csvContent += "\n";

        sortedDates.forEach(date => {
            let row = `${date}`;
            tradeData.forEach(r => { const point = r.data.find(d => d.Date === date); row += `,${point ? point.value : ''}`; });
            if (stockData) stockData.forEach(s => { const point = s.data.find(d => d.Date === date); row += `,${point ? point.price : ''}`; });
            csvContent += row + "\n";
        });

        const isExcel = format === 'excel';
        const blob = new Blob([isExcel ? '\uFEFF' + csvContent : csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = getFileName('csv'); link.click();
    };

    // --- [수정됨] 차트 옵션 생성 (unit 전달 및 심볼 사이즈 적용) ---
    const getChartOption = useCallback((data) => {
        if (!data || !isDateReady) return {};
        
        const { tradeData, stockData, name, hscode, unit } = data;
        const hasStock = stockData && stockData.length > 0;

        // 1. 왼쪽 축(Stock) 구성
        let finalLeftAxis = {
            name: hasStock ? '주가 (천원)' : '',
            type: 'log',
            isStock: true, 
            series: hasStock ? stockData.map(stock => ({
                name: stock.name,
                data: stock.data.map(d => [d.Date, d.price]),
                lineStyle: { width: 2.5, type: 'dotted' }
            })) : []
        };

        // 2. 오른쪽 축(Export) 구성
        let finalRightAxis = {
            name: unit === "%, YoY" ? `수출 성장률(${unit})` : '수출액(백만달러)',
            unit: unit, // [중요] unit 정보('%, YoY' 등)를 반드시 넘겨야 함
            series: tradeData.map(regionData => ({
                name: regionData.regionName,
                data: regionData.data.map(d => [d.Date, d.value]),
                showSymbol: true,
                lineStyle: { width: 2.5 }
            }))
        };

        // 3. 주가가 없을 때 수출 데이터를 왼쪽으로 이동
        if (!hasStock) {
            finalLeftAxis = {
                ...finalRightAxis, // 오른쪽 축의 설정(unit, series 등)을 모두 가져옴
                type: 'value',     // 타입은 value로 고정
                isStock: false     // 주가가 아님을 명시
            };
            finalRightAxis = { series: [] }; // 오른쪽 비움
        }

        // 4. 제목 및 옵션 생성
        const chartTitleText = isAggregated 
            ? `"${name}" 지역별 수출 합계` 
            : (hasStock 
                ? `"${stockData[0].name}" 등 주가 vs "${name}" 지역별 수출` 
                : `"${name}" 지역별 수출액 `);
        
        const titleConfig = { 
            text: chartTitleText, 
            subtext: `HS Code: ${hscode}`, 
            left: 'center', 
            textStyle: { fontSize: "1.2rem", fontWeight: 'bold' } 
        };

        return createDynamicDualAxisOptions({
            leftAxis: finalLeftAxis,
            rightAxis: finalRightAxis,
            title: titleConfig,
            dataZoom: [ { type: 'slider', bottom: '10rem', startValue: viewStartDate?.toISOString(), endValue: viewEndDate?.toISOString() } ]
        });

    }, [isDateReady, viewStartDate, viewEndDate, isAggregated]);


    if (initLoading) {
        return <Loading />;
    }

    return (
        <div className="content-box" style={{ maxWidth: '75rem', margin: '0.5 0.5rem', padding: '0.5rem' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>
                    지역별 품목 수출 데이터
                </Title>
                
                <Card>
                    <Title level={4}><SearchOutlined style={{ marginRight: '0.5rem' }}/>1. 분석할 품목 선택</Title>
                    <Select
                        showSearch
                        value={hscode}
                        options={hscodeOptions}
                        style={{ width: '100%' }}
                        onChange={setHscode}
                        placeholder="HS Code 또는 품목명을 입력하여 검색하세요."
                        filterOption={(input, option) => {
                            const cleanInput = input.replace(/[^a-zA-Z0-9가-힣]/g, "").toLowerCase();
                            const cleanLabel = (option?.label ?? "").toString().replace(/[^a-zA-Z0-9가-힣]/g, "").toLowerCase();
                            return cleanLabel.includes(cleanInput);
                        }}
                        allowClear
                    />
                </Card>
                <Card>
                     <Title level={4}><GlobalOutlined style={{ marginRight: '0.5rem' }}/>2. 비교할 지역 선택</Title>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                            <Space direction="vertical" style={{ minWidth: '12.5rem', flex: 1 }}>
                                <Text strong>광역지자체</Text>
                                <Select
                                    style={{ width: '100%' }} placeholder="광역지자체 선택" options={sidoOptions}
                                    onChange={handleSidoChange} value={sido} showSearch
                                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                />
                            </Space>
                            <Space direction="vertical" style={{ minWidth: '12.5rem', flex: 1 }}>
                                <Text strong>기초자치단체</Text>
                                <Select
                                    style={{ width: '100%' }} placeholder="기초자치단체 선택" options={sggOptions}
                                    value={sgg} onChange={setSgg} disabled={!sido || sido === '전체'} showSearch
                                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                />
                            </Space>
                        </div>
                        <Button type="primary" onClick={handleAddRegion}>지역 추가</Button>
                    </div>
                    <Divider/>
                    <Title level={5}>선택된 지역 목록</Title>
                    <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '0.5rem', minHeight: '3.75rem' }}>
                        {selectedRegions.length > 0 ? (
                            <Space size={[8, 16]} wrap>
                                {selectedRegions.map(region => (
                                    <Tag key={region.id} closable onClose={() => handleRemoveRegion(region.id)} style={{ fontSize: '0.875rem', padding: '0.375rem 0.625rem', display: 'flex', alignItems: 'center' }}>
                                        <EnvironmentOutlined style={{ marginRight: '0.5rem', color: '#1890ff' }}/> 
                                        {region.sido === '전체' ? '전국(전체)' : `${region.sido} ${region.sgg}`}
                                    </Tag>
                                ))}
                            </Space>
                        ) : ( <Text type="secondary">오른쪽 위에서 조회할 지역을 선택하고 '지역 선택' 버튼을 눌러주세요.</Text> )}
                    </div>
                </Card>
                <Card>
                    <Title level={4}><StockOutlined style={{ marginRight: '0.5rem' }}/>3. (선택) 주가 데이터 추가</Title>
                    <Select
                        mode="multiple"
                        showSearch allowClear optionFilterProp="label" value={selectedStocks}
                        onChange={setSelectedStocks} style={{ width: '100%' }}
                        placeholder="주식 종목을 선택하여 차트에 추가" options={stockOptions}
                    />
                </Card>

                {(hscode && selectedRegions.length > 0) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 0rem', margin : '0 0rem' }}>
                        <Button.Group size="small">
                            <Tooltip title="차트 이미지 저장">
                                <Button icon={<CameraOutlined />} onClick={handleDownloadPNG}>IMG</Button>
                            </Tooltip>
                            <Tooltip title="엑셀로 저장">
                                <Button icon={<FileExcelOutlined style={{ color: '#1D6F42' }} />} onClick={() => handleDownloadData('excel')}>XLS</Button>
                            </Tooltip>
                            <Tooltip title="CSV로 저장">
                                <Button icon={<FileTextOutlined style={{ color: '#217346' }} />} onClick={() => handleDownloadData('csv')}>CSV</Button>
                            </Tooltip>
                        </Button.Group>
                    </div>
                )}

                {(hscode && selectedRegions.length > 0) ? (
                    <Card bodyStyle={{ padding: '0.625rem' }}>
                        <div style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 10 }}>
                                <Checkbox 
                                    checked={isAggregated} 
                                    onChange={e => setIsAggregated(e.target.checked)}
                                    style={{ background: 'rgba(255, 255, 255, 0.7)', padding: '0.125rem 0.3125rem', borderRadius: '0.25rem' }}
                                >
                                    지역 데이터 합산
                                </Checkbox>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '2rem', gap: '1rem' }}>
                            <Radio.Group value={dataType} onChange={e => setDataType(e.target.value)} size="small">
                                <Radio.Button value="Value">값</Radio.Button>
                                <Radio.Button value="Growth">성장률</Radio.Button>
                            </Radio.Group>
                            <Radio.Group value={periodType} onChange={e => setPeriodType(e.target.value)} size="small">
                                <Radio.Button value="Monthly">월별</Radio.Button>
                                <Radio.Button value="TTM">TTM</Radio.Button>
                            </Radio.Group>
                        </div>

                        <div style={{ position: 'relative' }}>
                            <ReactECharts 
                                ref={chartRef}
                                option={getChartOption(processedChartData)} 
                                style={{ height: '31.25rem', width: '100%' }} 
                                notMerge={true} 
                                lazyUpdate={true} 
                                showLoading={loading} 
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.625rem' }}>
                            <Checkbox checked={isShifted} onChange={e => setIsShifted(e.target.checked)}>
                                수출 데이터 15일 시프트
                            </Checkbox>
                        </div>
                        <Divider />
                        <DateRangeControls
                            minDate={minDate}
                            maxDate={maxDate}
                            viewStartDate={viewStartDate}
                            viewEndDate={viewEndDate}
                            onPeriodChange={handlePeriodChange}
                            onDateChange={handleDateChange}
                        />
                    </Card>
                ) : (
                    <Card><Empty description={<span>분석할 품목과 지역을 선택하시면 차트가 자동으로 표시됩니다.</span>}/></Card>
                )}
            </Space>
        </div>
    );
};

export default ExportChartByRegionPage;